import { Link } from "react-router-dom";
import { isAndroidApp } from "@/lib/platform";
import { Lock, Sparkles, Crown } from "lucide-react";
import { useBilling } from "@/context/BillingContext";

const REQUIRED = { signal_full: "pro", track_record: "pro", alerts: "pro", indicators: "pro", bracket: "pro", journal: "pro", backtest: "premium", tuning: "premium", case_studies: "premium" };
const NAME = { pro: "Pro", premium: "Premium" };

const STORE_NOTE = "Available to Pro and Premium subscribers.";
// Apple 3.1.1 / Google Play Payments policy: store builds never point to an outside purchase — entitlement wording only.
export const webOnlyNote = () => STORE_NOTE;

export const UpgradeButton = ({ plan = "pro", size = "sm", className = "", testid = "upgrade-btn", label }) => isAndroidApp() ? null : (
  <Link to={`/pricing?plan=${plan}`} data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-lg font-semibold transition-colors ${size === "sm" ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"} ${plan === "premium" ? "bg-amber-500/15 text-amber-200 border border-amber-800 hover:bg-amber-500/25" : "btn-primary border border-transparent"} ${className}`}>
    {plan === "premium" ? <Crown className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
    {label || `Upgrade to ${NAME[plan]}`}
  </Link>
);

// Wrap any paid-feature block. Shows the real children when entitled; otherwise a locked card with an upgrade CTA.
export const Locked = ({ feature, title, desc, children, compact = false, testid }) => {
  const { can, loading } = useBilling();
  if (loading) return <div className="skeleton h-24" />;
  if (can(feature)) return children;
  const plan = REQUIRED[feature] || "pro";
  return (
    <div data-testid={testid || `locked-${feature}`} className={`apx-card relative overflow-hidden ${compact ? "p-4" : "p-6"}`}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: plan === "premium" ? "radial-gradient(120% 80% at 100% 0%, rgba(245,158,11,0.10), transparent 60%)" : "radial-gradient(120% 80% at 100% 0%, rgba(6,182,212,0.12), transparent 60%)" }} />
      <div className="relative flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${plan === "premium" ? "bg-amber-500/10 border-amber-900/60 text-amber-300" : "bg-cyan-500/10 border-cyan-900/60 text-cyan-300"}`}><Lock className="w-4 h-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">{NAME[plan]} feature</div>
          <div className="font-display font-semibold mt-0.5">{title}</div>
          {desc && <div className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</div>}
          <div className="mt-3">{isAndroidApp() ? <div className="text-xs text-slate-400" data-testid={`upgrade-${feature}-web-only`}>{webOnlyNote()}</div> : <UpgradeButton plan={plan} testid={`upgrade-${feature}`} />}</div>
        </div>
      </div>
    </div>
  );
};

export const PlanBadge = ({ plan }) => (
  <span data-testid="plan-badge" className={`text-[10px] font-mono-ticker uppercase tracking-widest px-2 py-0.5 rounded border ${plan === "premium" ? "bg-amber-500/15 text-amber-300 border-amber-800" : plan === "pro" ? "bg-cyan-500/15 text-cyan-300 border-cyan-800" : "bg-slate-800 text-slate-300 border-slate-700"}`}>{plan}</span>
);
